import { useContext } from "react";

import clsx from "clsx";
import {
  BooleanParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import { FETCH_SETTINGS_DETAILS } from "@/queries";
import { useNorthbeamLazyQuery } from "@hooks/http";
import {
  type FetchSettingsDetails,
  type GetSteps_me_onboardingSteps,
  type OnboardingStepType,
  type Profile as ProfileQuery,
} from "@north-beam/nb-client-side";

import { SideNavContext, type SubNavItem } from "..";
import { PROFILE_QUERY } from "../core/user-profile-collection-step";
import { Steps, type NavConfig, type Step } from "../steps";

export interface OnboardingNavItemProps {
  readonly step: Omit<Step, "navConfig"> & {
    readonly navConfig: Exclude<NavConfig, null>;
  };
  readonly isActive: boolean;
  readonly onClick: () => void;
  readonly stepNumber: number;
  readonly disabled: boolean;
  readonly subNav: { label: string; subStep: string }[];
}

const statusIcon = (
  isCompleted: boolean,
  stepNumber: number,
  started: boolean,
) => {
  if (!isCompleted && started) {
    return <i className="fa-solid fa-spinner text-white" />;
  } else if (isCompleted) {
    return <i className="fa-solid fa-check text-white"></i>;
  } else {
    return <span>{stepNumber}</span>;
  }
};

export const useSideNavContext = () => {
  const context = useContext(SideNavContext);

  if (!context) {
    throw new Error("useSideNav can only be used inside FiltersProvider");
  }

  return context;
};

export const useSideNavigation = () => {
  const [searchParams, setSearchParams] = useQueryParams({
    subStep: withDefault(StringParam, ""),
    noScroll: withDefault(BooleanParam, false),
  });
  return [searchParams, setSearchParams] as const;
};

export const OnboardingNavItem = ({
  step,
  isActive,
  onClick,
  stepNumber,
  disabled,
  onboardingV2Steps,
}: OnboardingNavItemProps & {
  onboardingV2Steps: {
    [key in OnboardingStepType]?: GetSteps_me_onboardingSteps;
  };
}): JSX.Element => {
  const context = useSideNavContext();

  const [searchParams, setSearchParams] = useQueryParams({
    subStep: withDefault(StringParam, ""),
    noScroll: withDefault(BooleanParam, false),
  });
  const {
    loadData: loadAccountSettings,
    data: { data: accountData, loading: loadingSettings, error },
  } = useNorthbeamLazyQuery<FetchSettingsDetails>(FETCH_SETTINGS_DETAILS, {
    fetchPolicy: "network-only",
  });

  const {
    loadData: loadProfileData,
    data: { data: profileData, loading: loadingSettingData },
  } = useNorthbeamLazyQuery<ProfileQuery>(PROFILE_QUERY, {
    fetchPolicy: "network-only",
  });

  const persistedStep =
    onboardingV2Steps[
      step.value.replaceAll("-", "_").toUpperCase() as OnboardingStepType
    ];

  const isStepCompleted = () => {
    switch (step.value) {
      case Steps.INTRODUCTION:
        return !!persistedStep?.start_time;
      case Steps.ACCOUNT_MANAGERS:
        if (!accountData && !loadingSettings) {
          loadAccountSettings();
        }
        return !!persistedStep?.start_time;
      case Steps.BASIC_DETAILS:
        if (!profileData && !loadingSettingData) {
          loadProfileData();
        }
        return (
          !!profileData?.me.companyName &&
          !!profileData.me.timezone &&
          persistedStep?.start_time
        );
      case Steps.DOMAIN:
        if (!profileData && !loadingSettingData) {
          loadProfileData();
        }
        return !!persistedStep?.start_time && !!profileData?.me.domainName;
      default:
        return !!persistedStep?.start_time && !!persistedStep.end_time;
        break;
    }
  };

  const isFirstStep = stepNumber === 1;
  const isLastStep = step.value === Steps.COMPLETE;

  return (
    <>
      {!isFirstStep && ( // "nav-spacer" element from designs
        <div className="self-stretch h-1 rounded-lg justify-start items-center inline-flex">
          <div className="w-7 self-stretch flex-col justify-center items-center inline-flex">
            <div className="w-0.5 grow shrink basis-0 bg-gray-0" />
          </div>
          <div className="grow shrink basis-0 self-stretch px-2 rounded-lg" />
        </div>
      )}
      <button
        disabled={disabled}
        key={step.value}
        onClick={() => onClick()}
        className={clsx("w-full flex items-center h-10 z-3")}
      >
        <div className="self-stretch flex-col justify-center items-center inline-flex">
          <div
            className={clsx("w-0.5 grow shrink basis-0", {
              "bg-gray-0": stepNumber > 1,
            })}
          />
          <div
            className={clsx(
              "w-7 h-7 rounded-full border-2 z-3 text-sm items-center justify-center flex",
              {
                "bg-white":
                  !persistedStep?.start_time && !persistedStep?.end_time,
                "bg-alien+0 border-alien+0":
                  (persistedStep?.start_time || persistedStep?.end_time) &&
                  !isActive,
                "border-gray+1":
                  !isActive &&
                  !persistedStep?.start_time &&
                  !persistedStep?.end_time,
                "bg-cosmic border-cosmic": isActive,
                "bg-sun border-sun":
                  persistedStep?.start_time && !isStepCompleted() && !isActive,
              },
            )}
          >
            {statusIcon(
              isStepCompleted(),
              stepNumber,
              !!persistedStep?.start_time,
            )}
          </div>
          <div
            className={clsx("w-0.5 grow shrink basis-0", {
              "bg-gray-0": !isLastStep,
            })}
          />
        </div>
        <div
          className={clsx("z-3 w-2 h-[2px] bg-primary", {
            invisible: !isActive,
          })}
        />
        <div
          className={clsx(
            "w-full flex items-center h-[38px] pl-2 rounded-lg ",
            {
              "hover:bg-gray-1": !isActive && !disabled,
              "bg-cosmic+0 hover:bg-cosmic+1": isActive && !disabled,
              "text-gray+1": disabled,
            },
          )}
        >
          {disabled ? (
            <i className="w-[18px] fa-sharp fa-regular fa-lock font-black text-base" />
          ) : (
            <i
              className={clsx(
                "fa-regular h-5 w-[18px]",
                `fa-${step.navConfig.icon}`,
                {
                  "text-white": isActive,
                  "text-cosmic": !isActive,
                },
              )}
            />
          )}
          <div
            className={clsx("ml-3", {
              "text-white": isActive,
            })}
          >
            {step.navConfig.label}
          </div>
        </div>
      </button>
      {isActive && context.subNavItems?.length > 0 && (
        <div className="flex flex-col z-3 w-full">
          {context.subNavItems?.map((subNavItem: SubNavItem) => (
            <button
              key={subNavItem.subStep}
              onClick={() => {
                setSearchParams({
                  subStep: subNavItem.subStep,
                  noScroll: false,
                });
              }}
              className="group w-full h-8 rounded-lg justify-start items-center inline-flex"
            >
              <div className="w-7 h-full flex-col justify-center items-center inline-flex flex-shrink-0">
                <div className="w-0.5 grow shrink basis-0 bg-gray-0" />
                <div
                  className={clsx("w-2 h-2 rounded-lg", {
                    "bg-cosmic": searchParams.subStep === subNavItem.subStep,
                    "bg-white": searchParams.subStep !== subNavItem.subStep,
                    "border-gray-0":
                      searchParams.subStep !== subNavItem.subStep,
                    "border-2": searchParams.subStep !== subNavItem.subStep,
                  })}
                />
                <div className="w-0.5 grow shrink basis-0 bg-gray-0" />
              </div>
              <div
                className={clsx(
                  "group",
                  "px-2 rounded-lg justify-start items-center gap-3 group-hover:text-cosmic",
                  "text-sm font-normal leading-tight",
                  "max-w-full overflow-hidden whitespace-nowrap text-ellipsis",
                  {
                    "grow shrink self-stretch basis-0 flex":
                      typeof subNavItem.label !== "string",
                    "group-hover:underline":
                      typeof subNavItem.label === "string",
                  },
                )}
              >
                {typeof subNavItem.label === "string" ? (
                  subNavItem.label
                ) : (
                  <subNavItem.label />
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </>
  );
};
